/**
 * Created by Rodrigo Quesada on 16/02/17.
 */
import {ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {Observable, ReplaySubject} from 'rxjs';
import {StepScreenModel} from '../step-screen.component';
import {FormErrors} from '../../../../components/forms/validation/form-validator.class';
import {AccordionComponent} from '../../../../components/accordion/accordion.component';
import {Response, ResponseOptions} from '@angular/http';
import {merge} from 'rxjs/observable/merge';
import {Model} from '../../../../domain/model/common/model.class';
import {UserIdentifiedModelListService} from '../../../../domain/model/common/user-identified-model-list.service';
import {IdentifiedModel} from '../../../../domain/model/common/identified-model.class';
import {Entry} from './entry.class';
import _ = require('lodash');

//region Utils
function _defaultToEmpty(value: string | null): string {
    return value != null ? value : '';
}

//endregion

const REMOVE = Symbol();

export abstract class AccordionScreen<E extends Entry<M>, M extends IdentifiedModel<M>> {

    constructor(protected readonly _modelType: Model.Type<M>,
                protected readonly _createEntryType: (M) => E,
                protected readonly _headlineProperty: keyof M,
                protected readonly _modelService: UserIdentifiedModelListService<M>) {

        this.stepScreenModel$.subscribe(m => this.formErrors = m.formErrors);

        this._initEntriesLogic();

        // Initial Dummy Data
        this.stepScreenModel$.next(new StepScreenModel<M>({
            initialDataModel: new _modelType(),
            specificValidationMessages: {},
            _loadDataFn: () => {
                return Observable.of(new _modelType());
            },
            _submitDataFn: (model: M) => {
                return Observable.of(new Response({} as ResponseOptions));
            }
        }));
        this.processedForm$.next(new NgForm([], []));
    }

    // region Form
    readonly form$ = new ReplaySubject<NgForm | undefined>(1);
    readonly processedForm$ = new ReplaySubject<NgForm>(1);

    @ViewChild(NgForm) set form(value: NgForm) {
        this.form$.next(value);
    }

    // endregion

    stepScreenModel$ = new ReplaySubject<StepScreenModel<M>>(1);

    formErrors: FormErrors;

    //region Entries Logic
    entries: E[] = [];
    newEntryValue: string = '';

    private readonly _accordion$ = new ReplaySubject<AccordionComponent | undefined>(1);
    private readonly _addedEntryIndex$ = new ReplaySubject<number>(1);
    private readonly _movedEntryIndex$ = new ReplaySubject<number | symbol>(1);
    private _entryIndexChange$: Observable<number | undefined | symbol>;

    currentEntryIndex$: Observable<number | undefined | symbol>;

    @ViewChild(AccordionComponent) set accordion(value: AccordionComponent | undefined) {
        this._accordion$.next(value);
    }

    private _initEntriesLogic() {
        this._modelService.getCurrentUserModelEntryList().subscribe(values => {
            return this.entries = values.sort(this._sortModels).map(it => this._createEntryType(it));
        });

        this._entryIndexChange$ = merge(this._accordion$.filter(it => !!it).switchMap(it => it!.openGroup$), this._movedEntryIndex$)
            .distinctUntilChanged()
            .publishReplay(1).refCount();

        this.currentEntryIndex$ = merge(this._entryIndexChange$, this._addedEntryIndex$)
            .distinctUntilChanged()
            .publishReplay(1).refCount();

        this.processedForm$.withLatestFrom(this._entryIndexChange$)
            .subscribe(([, index]) => {
                if (_.isNumber(index)) {
                    this.stepScreenModel$.next(new StepScreenModel<M>({
                        initialDataModel: new this._modelType(),
                        specificValidationMessages: {},
                        _loadDataFn: () => {
                            return Observable.of(this.entries[index].model);
                        },
                        _submitDataFn: (model: M) => this._saveModel(model)
                    }));
                }
            });

        const formPairwise$: Observable<[NgForm | undefined, NgForm | undefined]> = this.form$.distinctUntilChanged().startWith(undefined).pairwise();
        const currentEntryIndexPairwise$: Observable<[number | symbol | undefined, number | symbol | undefined]> = this._entryIndexChange$.startWith(undefined).pairwise();

        formPairwise$.withLatestFrom(currentEntryIndexPairwise$)
            .subscribe(([[previousForm, form], [previousIndex, index]]) => {
                if (previousForm && previousForm.dirty && _.isNumber(previousIndex) && index !== REMOVE) {
                    this._saveEntry(this.entries[previousIndex]).subscribe();
                }

                if (form) setTimeout(() => this.processedForm$.next(form));
            });
    }

    private _saveEntry(entry: E): Observable<Response> {
        return this._saveModel(entry.model).do(res => entry.update(res));
    }

    private _saveModel(model: M): Observable<Response> {
        return this._modelService.updateCurrentUserModelEntry(model);
    }

    addEntry() {
        if (!_.isEmpty(this.newEntryValue)) {
            const newEntry = this._createEntryType(new this._modelType(this._newEntryData(this.newEntryValue)));
            this.entries.push(newEntry);
            this._saveEntry(newEntry).subscribe();
            this.newEntryValue = '';
            this._addedEntryIndex$.next(this.entries.length - 1);
        }
    }

    remove(entry: E) {
        if (confirm(this._createConfirmationMessage(entry))) {
            const index = this.entries.indexOf(entry);
            const [removedEntry] = this.entries.splice(index, 1);
            if (removedEntry.model.id) this._modelService.deleteCurrentUserModelEntry(removedEntry.model).subscribe();
            this._entryIndexChange$.first().subscribe(currentIndex => {
                if (_.isNumber(currentIndex)) {
                    if (currentIndex === index) {
                        this._movedEntryIndex$.next(REMOVE);
                    }
                    else if (currentIndex > index) {
                        this._movedEntryIndex$.next(REMOVE);
                        this._movedEntryIndex$.next(currentIndex - 1);
                    }
                }
            });
        }
    }

    private _newEntryData(value: string): any { return {[this._headlineProperty]: value}; }

    private _sortModels = (a, b) => _defaultToEmpty(a[this._headlineProperty]).localeCompare(_defaultToEmpty(b[this._headlineProperty]));

    private _createConfirmationMessage(entry: E): string {
        return `Are you sure you want to delete this entry? [${entry.model[this._headlineProperty]}]`;
    }

    //endregion
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/pages/shared/step/accordion/accordion-screen.class.ts